import { useNavigate, useParams } from 'hotel/navigation';
import { useEffect } from 'react';

import { SectionHeader } from 'components/SectionHeader';
import { useBookingQuery } from 'generated/graphql';
import { useUserRole } from 'hooks/useUserRole';
import { useAppSelector } from 'store/store';

import GuestCard from './components/GuestCard';
import InvoiceCard from './components/InvoiceCard';
import SendConfirmationEmail from './components/SendConfirmationEmail';
import YourTripCard from './components/YourTripCard';

const BookingConfirmation = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams<{ bookingId: string }>();
  const { isOnlyTraveler } = useUserRole();
  const showTravelerPayments = useAppSelector(
    (state) => state.enums.featureFlags?.flags.hotelSavedCards === true,
  );

  // Use the booking query if we have a bookingId from URL params
  const {
    data: bookingQueryData,
    loading,
    error,
  } = useBookingQuery({
    variables: {
      bookingId: bookingId || '',
      includeTravelerPayments: showTravelerPayments,
    },
    skip: !bookingId,
  });

  // Determine which booking data to use
  const booking = bookingQueryData?.booking;

  // Redirect if no booking data and no loading state
  useEffect(() => {
    if (!loading && !booking && !error) {
      navigate('/trips');
    }
  }, [booking, loading, error, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-lg">Loading booking details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-lg text-red-600">
          Error loading booking: {error.message}
        </div>
      </div>
    );
  }

  if (!booking) {
    return null; // Will redirect via useEffect
  }

  // Booking data loaded successfully

  return (
    <div className="flex flex-col items-center p-5 sm:px-28">
      <div className="w-full max-w-7xl">
        <SectionHeader
          headline="YOUR TRIP HAS BEEN BOOKED!"
          className="flex-col items-start space-y-[10px] font-oswald "
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* Left side - Main content */}
          <div className="col-span-2 space-y-6">
            <InvoiceCard booking={booking} />
            <GuestCard booking={booking} filterToCurrentUser={isOnlyTraveler} />
          </div>

          {/* Right side - Your Trip card and Send Email */}
          <div className="space-y-6 md:sticky md:top-6 md:col-start-3 md:row-span-3 md:row-start-1 md:h-fit">
            <YourTripCard
              booking={booking}
              hideRoomGuestCounts={isOnlyTraveler}
            />
            {!isOnlyTraveler && <SendConfirmationEmail booking={booking} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
